"use client";
import React, { createContext, useContext } from "react";
import { getTeamConfigs } from "src/shared/teamConfigs/teamConfigs";
import { NextPageContext } from "next";
import App, { AppContext } from "next/app";
import { TTeamConfig } from "src/shared/types/teamConfig.types";

/**
 * ------------------------------------------------------------
 *
 * Team Config Provider :
 * serves the team config across the app.
 * teamSchema string is bound from server side. check __app.tsx
 *
 * ------------------------------------------------------------
 */

// binding teamSchema to the app from server side
export const bindServerSideTeamSchema = async (ctx: AppContext) => {
  const appProps = await App.getInitialProps(ctx);
  let teamSchema = ctx?.router?.query?.teamSchema as string;
  // TODO : must remove this default teamSchema and make it dynamic
  if (!teamSchema) teamSchema = "pass_test";
  appProps.pageProps.teamSchema = teamSchema;
  return appProps;
};

// context and hooks to serve the team config across the app
export const TeamConfigCtx = createContext<TTeamConfig | null>(null);
export const useTeamConfig = () => useContext(TeamConfigCtx) as TTeamConfig;

/**
 * Resolves the config for a schema coming from the URL.
 *
 * Falls back to a case-insensitive match because the schema is typed into
 * third-party deep link configuration we do not control, and a casing mismatch
 * used to take the whole app down. Schemas are unique lowercase identifiers, so
 * the relaxed match cannot collide.
 */
function resolveTeamConfig(urlTeamSchema: string) {
  const configs = getTeamConfigs();

  const exact = configs.find((c) => c?.TEAM_SCHEMA === urlTeamSchema);
  if (exact) return { teamConfig: exact, matchedBy: "exact", configs };

  const schema = urlTeamSchema?.toLowerCase();
  const relaxed = configs.find((c) => c?.TEAM_SCHEMA?.toLowerCase() === schema);
  if (relaxed) return { teamConfig: relaxed, matchedBy: "case-insensitive", configs }; // prettier-ignore

  return { teamConfig: null, matchedBy: "none", configs };
}

/**
 * Rendered instead of the app when no config resolves.
 *
 * Deliberately plain: no MUI, no theme, no hooks. Everything in this app's
 * design system reads the team config, so any of it would throw here for the
 * exact reason this screen exists.
 */
function TeamConfigUnavailable(props: { teamSchema: string; registrySize: number }) { // prettier-ignore
  const isProduction = process.env.NODE_ENV === "production";
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        padding: 24,
        textAlign: "center",
        background: "#ffffff",
        color: "#111111",
        fontFamily: "-apple-system, Helvetica, Arial, sans-serif",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600 }}>This page is unavailable</div>
      <div style={{ fontSize: 14, color: "#555555" }}>
        We could not load this program. Please try again.
      </div>
      {!isProduction ? (
        <pre style={{ fontSize: 11, color: "#777777", marginTop: 16 }}>
          {`teamSchema: ${props.teamSchema || "(vazio)"}\nregistry: ${props.registrySize}`}
        </pre>
      ) : null}
    </div>
  );
}

// context provider
export function TeamConfigProvider(props: {
  children: React.ReactNode;
  pageProps: NextPageContext & { teamSchema?: string };
}) {
  const urlTeamSchema = props.pageProps?.teamSchema as string;

  // Resolved here, at render time, instead of at module scope: the import cycle
  // between teamConfigs and TeamConfigProvider left the registry incomplete when
  // it was evaluated too early on a cold start.
  const { teamConfig, matchedBy, configs } = resolveTeamConfig(urlTeamSchema);

  if (matchedBy === "case-insensitive") {
    // Worth knowing about: something is linking to this app with the wrong casing.
    console.warn(
      `[TeamConfigProvider] schema matched case-insensitively: "${urlTeamSchema}" -> "${teamConfig?.TEAM_SCHEMA}"`,
    );
  }

  if (!teamConfig) {
    // These two numbers separate the two known causes: a registry size well below
    // the expected count means the modules had not finished initializing (cold
    // start + import cycle); a full registry means the schema itself is wrong.
    console.error(
      `[TeamConfigProvider] no config for schema "${urlTeamSchema}" · registry=${configs.length} · server=${typeof window === "undefined"}`,
    );
    return (
      <TeamConfigUnavailable
        teamSchema={urlTeamSchema}
        registrySize={configs.length}
      />
    );
  }

  return (
    <TeamConfigCtx.Provider value={teamConfig}>
      {props.children}
    </TeamConfigCtx.Provider>
  );
}
